/**
 * Core-backed SDP catalog adapter for the two-categorical CSK demo.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { z } from "zod";
import {
  COMPOSITIONALITY_PRINCIPLE_CODES,
  COMPOSITIONALITY_PRINCIPLE_NAMES,
  DUAL_PAIR_GUARANTEE_NAMES,
  getCompositionalityPrincipleMeta,
  getImmunityClosureGuaranteeNameForSdp,
  isNeutralizationTheoremCode,
  type CompositionalityPrincipleCode,
  type CompositionalityPrincipleName,
  type DualPairGuaranteeName,
  type ImmunityClosureGuaranteeName,
  type NeutralizationTheoremCode,
} from "@csk/core";
import sdpData from "./sdps.json" with { type: "json" };

const sdpSchema = z.object({
  code: z.enum(COMPOSITIONALITY_PRINCIPLE_CODES),
  name: z.enum(COMPOSITIONALITY_PRINCIPLE_NAMES),
  adjunction: z.string(),
  neutralizationTheorems: z.array(z.string()),
  dualPairGuarantee: z.enum(DUAL_PAIR_GUARANTEE_NAMES).nullable(),
  icg: z.string(),
  color: z.string(),
  aiScenario: z.string(),
  aiSummary: z.string(),
  unitDescription: z.string(),
  counitDescription: z.string(),
  concreteAttack: z.string(),
});

interface SdpJsonEntry {
  readonly code: CompositionalityPrincipleCode;
  readonly name: CompositionalityPrincipleName;
  readonly adjunction: string;
  readonly neutralizationTheorems: readonly string[];
  readonly dualPairGuarantee: DualPairGuaranteeName | null;
  readonly icg: string;
  readonly color: string;
  readonly aiScenario: string;
  readonly aiSummary: string;
  readonly unitDescription: string;
  readonly counitDescription: string;
  readonly concreteAttack: string;
}

export interface SdpCatalogEntry extends SdpJsonEntry {
  readonly icg: ImmunityClosureGuaranteeName;
  readonly neutralizationTheoremCodes: readonly NeutralizationTheoremCode[];
}

function theoremCodeFromLabel(label: string): NeutralizationTheoremCode {
  const code = label.split(" ", 1)[0] ?? "";
  if (!isNeutralizationTheoremCode(code)) {
    throw new Error(
      `Unknown Neutralization Theorem label in SDP catalog: ${label}`,
    );
  }
  return code;
}

function sameList<T>(left: readonly T[], right: readonly T[]): boolean {
  return (
    left.length === right.length &&
    left.every((item, index) => item === right[index])
  );
}

function assertSdpMatchesCore(entry: SdpJsonEntry): SdpCatalogEntry {
  const core = getCompositionalityPrincipleMeta(entry.code);
  const icg = getImmunityClosureGuaranteeNameForSdp(entry.code);
  const theoremCodes = entry.neutralizationTheorems.map(theoremCodeFromLabel);
  if (
    entry.name !== core.name ||
    entry.icg !== icg ||
    !sameList(theoremCodes, core.theorems)
  ) {
    throw new Error(
      `Two-categorical SDP ${entry.code} drifted from @csk/core metadata`,
    );
  }
  return { ...entry, icg, neutralizationTheoremCodes: theoremCodes };
}

export const SDPS: readonly SdpCatalogEntry[] = z
  .array(sdpSchema)
  .parse(sdpData.sdps)
  .map(assertSdpMatchesCore);
